<!-- Dossier.vue -->
<template>
  <Stack
    class="component stk-py-8"
    gap="4"
  >
    <Level class="header">
      <LevelLeft>
        <Title size="8">
          Dossier
        </Title>
        <Subtitle size="6">
          The 'up-to-date' verified information
        </Subtitle>
      </LevelLeft>

      <LevelRight>
        <div class="header-buttons">
          <!-- <Button
            variant="primary"
            size="5"
            @click="navigateToFormUpload"
          >
            Update Dossier
          </Button> -->
          <Button
            variant="secondary"
            icon-before="fas fa-print"
            @click="navigateToFactSheet"
          >
            View Fact Sheet
          </Button>
          <Button
            v-if="advisorStore.featureFlags.includes('CRMMigration')"
            variant="secondary"
            icon-before="fas fa-exchange-alt"
            @click="navigateToCrmMigration"
          >
            CRM Migration
          </Button>
          <!-- <Button
            variant="primary"
            size="5"
            @click="navigateToFinancialPlan"
          >
            View Financial Plan
          </Button> -->
        </div>
      </LevelRight>
    </Level>

    <DossierUpdates
      class="updates"
      :entity-external-id="entityExternalId"
      :draft-dossier="draftDossier"
    />

    <DossierOnboarding
      class="onboarding"
      :ready="hasFetchedEntity"
      :meeting-count="meetingCount"
      :crm-contact-name="crmContactName"
      :entity-external-id="entityExternalId"
      :loading="isGeneratingDossier"
      :csrf-token="csrfToken"
      :has-dossier-content="hasDossierContent"
      @crm-contact-updated="updateCrmContact"
      @process-data="generateDossier"
    />

    <LoaderWithText v-if="isLoadingDossier">
      Loading Dossier…
    </LoaderWithText>

    <div
      v-else
      class="body"
    >
      <aside class="side">
        <Menu>
          <MenuList>
            <MenuItem
              v-for="menuItem in dossierStore.menuItems"
              :key="menuItem.id"
              :active="menuItem.id === topLevelPropertyId"
              @click="topLevelPropertyId = menuItem.id"
            >
              {{ menuItem.label }}
            </MenuItem>
          </MenuList>
        </Menu>

        <Stack
          class="print-box"
          gap="4"
        >
          <Stack gap="2">
            <label class="print-radio">
              <input
                v-model="printMode"
                type="radio"
                value="single"
              >
              Only print this section:
              <strong>{{ prettyTopLevelPropertyId }}</strong>
            </label>
            <label class="print-radio">
              <input
                v-model="printMode"
                type="radio"
                value="all"
              >
              Print <strong>all</strong> sections
            </label>
          </Stack>

          <label class="print-radio">
            <input
              v-model="printEmpty"
              type="checkbox"
            >
            Print empty fields
          </label>

          <Button
            variant="primary"
            @click="printDossier"
          >
            Print
          </Button>
        </Stack>
      </aside>

      <div class="main">
        <template v-if="topLevelPropertyId">
          <DossierItem
            :id="topLevelPropertyId"
            :ids="[]"
            :schema="dossierStore.resolvedProperties[topLevelPropertyId]"
            :depth="0"
            :array-index="0"
            @field-changed="handleFieldChanged"
          />
        </template>

        <!-- Save Button Fixed at Bottom -->
        <DossierSaveButton
          :entity-external-id="entityExternalId"
          @save-completed="handleSaveCompleted"
          @save-failed="handleSaveFailed"
        />
      </div>
    </div>
  </Stack>
</template>

<script>
import {
  Button,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Subtitle,
  Title,
} from '@stakeui';

import { mapStores } from 'pinia';
import { useDossierStore } from '../../../stores/dossierStore';
import DossierItem from '../components/DossierItem.vue';
import DossierOnboarding from '../components/DossierOnboarding.vue';
import DossierUpdates from '@frontend/ui/features/dossier/components/DossierUpdates.vue';
import DossierSaveButton from '../components/DossierSaveButton.vue';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import { prettifyMixedCase, getCurrentDateUSFormat } from '@frontend/shared/Util.js';
import * as Sentry from '@sentry/vue';
import { useAdvisorStore } from "@frontend/ui/stores/advisorStore.js";

export default {
  name: 'Dossier',
  components: {
    Button,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    Menu,
    MenuList,
    MenuItem,
    Stack,
    Title,
    Subtitle,
    DossierItem,
    DossierOnboarding,
    DossierSaveButton,
    DossierUpdates,
  },
  computed: {
    ...mapStores(useDossierStore, useAdvisorStore),
    csrfToken() {
      return this.$root.$data.csrfToken;
    },
    hasDossierContent() {
      // Check if the dossier has any content
      return Object.keys(this.dossierStore.currentValue).length > 0;
    },
    prettyTopLevelPropertyId() {
      return prettifyMixedCase(this.topLevelPropertyId);
    },
    displayedProperties() {
      return Object.keys(this.dossierStore.currentValue).filter(key => key !== 'metadata');
    },
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      topLevelPropertyId: '',
      isGeneratingDossier: false,
      hasFetchedEntity: false,
      isLoadingDossier: true,
      documentCount: 0,
      meetingCount: 0,
      crmContactName: null,
      entityData: null,
      editMode: false,
      printMode: 'all',
      printEmpty: false,
      draftDossier: {},
      hasDraftDossier: false,
      taskCheckInterval: null,
    };
  },
  async created() {
    // Fetch the template in case we need to generate an empty dossier
    await this.fetchDossierTemplate();
    // Then try to fetch existing dossier data
    await this.fetchEntityDossier();

    // Fetch entity data to get document count, meeting count, and CRM contact
    await this.fetchEntityData();

    if (!this.topLevelPropertyId && this.dossierStore.menuItems.length > 0) {
      this.topLevelPropertyId = this.dossierStore.menuItems[0].id;
    }
  },
  methods: {
    async fetchDossierTemplate() {
      const response = await fetch('/api/v1/dossier/template/');
      const data = await response.json();
      console.log(data);
      this.dossierStore.setSchema(data.schema);
    },
    async fetchEntityDossier() {
      try {
        const response = await fetch(`/api/v1/dossier/entity/${this.entityExternalId}/`);
        const data = await response.json();
        const template = data.template
        if (data.template) {
          this.dossierStore.setSchema(data.template);
        }

        const dossier = data.dossier;
        const latestDossierGenerationTaskStatus = data.latest_dossier_generation_task_status;
        if (latestDossierGenerationTaskStatus === 'pending' || latestDossierGenerationTaskStatus === 'started') {
          this.isGeneratingDossier = true;
          
          // Set up polling to check task status
          const taskId = data.latest_dossier_generation_task_id;
          // Poll for task status
          try {
            const pollForTaskStatus = async () => {
              const interval = setInterval(async () => {
                try {
                  const response = await fetch(`/api/v1/tasks/${taskId}/`, {
                    method: 'GET',
                    headers: {
                      'X-CSRFToken': this.$root.$data.csrfToken,
                      'Content-Type': 'application/json',
                    },
                  });
                  const data = await response.json();
                  
                  if (data.task?.status === 'failed') {
                    clearInterval(interval);
                    this.taskCheckInterval = null;
                    this.isGeneratingDossier = false;
                    console.error('Dossier generation failed:', data);
                    Sentry.captureException('Dossier generation failed:', data);

                    this.$buefy.toast.open({
                      message: 'Failed to generate dossier',
                      type: 'is-danger',
                      duration: 5000,
                    });
                    return;
                  }

                  const is_updated = data.task?.meta?.is_updated // make sure the dossier was actually updated before going to review mode
                  console.log('UPON RUNNING DOSSIER GENERATION TASK, WAS IT UPDATED?', is_updated)
                  // Task completed successfully
                  if (data.task?.status === 'complete' && is_updated == false){
                    clearInterval(interval);
                    this.taskCheckInterval = null;
                    this.isGeneratingDossier = false;
                    this.$buefy.toast.open({
                      message: 'No new data sources for Dossier-- no changes made!',
                      type: 'is-success',
                      duration: 5000,
                    });
                  }
                  else if (data.task?.status === 'complete') {
                    clearInterval(interval);
                    this.taskCheckInterval = null;
                    this.isGeneratingDossier = false;
                    console.log('Dossier generation complete:', data);
                    
                    if (data.task.meta.dossier_output) {
                      this.$router.push(`/advisor/entity/${this.entityExternalId}/reconciliation`);
                    }
                    return;
                  }
                } catch (error) {
                  clearInterval(interval);
                  this.taskCheckInterval = null;
                  this.isGeneratingDossier = false;
                  console.error('Error checking task status:', error);
                  Sentry.captureException(error);
                  
                  this.$buefy.toast.open({
                    message: 'Error checking dossier generation status',
                    type: 'is-danger',
                    duration: 5000,
                  });
                }
              }, 2000); // Check every 2 seconds
              
              // Store the interval ID so we can clear it if needed
              this.taskCheckInterval = interval;
              
              // Set a timeout to stop polling after 5 minutes
              setTimeout(() => {
                if (this.taskCheckInterval) {
                  clearInterval(this.taskCheckInterval);
                  this.taskCheckInterval = null;
                }
                if (this.isGeneratingDossier) {
                  this.isGeneratingDossier = false;
                  console.error('Dossier generation timed out');
                  
                  this.$buefy.toast.open({
                    message: 'Dossier generation timed out',
                    type: 'is-warning',
                    duration: 5000,
                  });
                }
              }, 3600000); // 1 hour timeout
            };
            
            pollForTaskStatus();
          } catch (error) {
            this.isGeneratingDossier = false;
            console.error('Error setting up polling for dossier generation task:', error);
            
            this.$buefy.toast.open({
              message: 'Failed to monitor dossier generation',
              type: 'is-danger',
              duration: 5000,
            });
            
            Sentry.captureException(error);
          }
        }
        // If a dossier exists, set it as the current value
        if (dossier?.dossier_data) {
          console.log('Found existing dossier:', dossier);
          this.dossierStore.setInitialValue(dossier.dossier_data);

          // Show toast notification
          this.$buefy.toast.open({
            message: `Loaded existing dossier`,
            type: 'is-info',
            duration: 3000,
          });
          
          // Check if there's a draft dossier
        } else {
          console.log('No existing dossier found for this entity. Generate an empty one.');
          const initialValue = this.dossierStore.generateEmptyObject(this.dossierStore.schema);
          this.dossierStore.setInitialValue(initialValue);
        }
      // Check if there's a draft dossier
      await this.checkForDraftDossier();
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        this.isLoadingDossier = false;
      }
    },
    async fetchEntityData() {
      try {
        const response = await fetch(`/api/v1/entity/${this.entityExternalId}/`);
        const data = await response.json();

        this.entityData = data;
        this.documentCount = data.document_count || 0;
        this.meetingCount = data.meeting_count || 0;
        const crmData = await this.fetchCrmContact(data.crm_type, data.crm_connection_id);
        this.crmContactName = crmData?.name;
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error fetching entity data:', error);
      } finally {
        this.hasFetchedEntity = true;
      }
    },
    async fetchCrmContact(crmType, crmConnectionId) {
      if (!crmType || !crmConnectionId) return null;

      try {
        const response = await fetch(`/api/v1/crm/${crmType.toLowerCase()}/contact/${crmConnectionId}/`, {
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          return data;
        } else {
          console.error('Error fetching CRM contact:', response.statusText);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error fetching CRM contact:', error);
      }

      return null;
    },
    async generateDossier() {
      this.isGeneratingDossier = true;
      try {
        // Show initial toast notification
        this.$buefy.toast.open({
          message: 'Dossier generation started. This may take a few moments.',
          type: 'is-info',
          duration: 5000,
        });

        // Call the generate_dossier API using awaitTask
        const result = await awaitTask('/api/v1/dossier/generate/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            entity_id: this.entityExternalId,
            is_draft: true,
          }),
        });

        const is_updated = result.task?.meta?.is_updated // make sure the dossier was actually updated before going to review mode
        console.log('IS UPDATED?', is_updated)
        // Task completed successfully
        if (result.task?.status === 'complete' && is_updated == false){
          this.$buefy.toast.open({
            message: 'No new data sources for Dossier-- no changes made!',
            type: 'is-success',
            duration: 5000,
          });
        }
        else if (result.task?.status === 'complete') {
          this.$buefy.toast.open({
            message: 'Dossier generation completed successfully!',
            type: 'is-success',
            duration: 5000,
          });
          // this.dossierStore.setInitialValue(result.task.meta.dossier_output);
          // Navigate to the reconciliation page for this entity
          this.$router.push(`/advisor/entity/${this.entityExternalId}/reconciliation`);
          // Refresh the data to show new dossier information
          // await this.fetchEntity();
          // await this.fetchDocuments();
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error generating dossier:', error);
        this.$buefy.toast.open({
          message: 'Failed to generate dossier: ' + (error.task?.meta?.error || error.message),
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.isGeneratingDossier = false;
      }
    },
    updateCrmContact() {
      // Refresh entity data to get the most up-to-date information
      this.fetchEntityData();

      // Show a success message
      this.$buefy.toast.open({
        message: 'CRM contact updated successfully',
        type: 'is-success',
        duration: 3000,
      });
    },
    handleFieldChanged() {
      // The dossier store will already have hasUnsavedChanges set to true
      // when the value is changed, so we don't need to do anything here
      // Ensure isEditing is set to true to display the save buttons
      this.dossierStore.setEditMode(true);
    },
    handleSaveCompleted(data) {
      this.$buefy.toast.open({
        message: 'Dossier saved successfully',
        type: 'is-success',
        duration: 3000,
      });
    },
    handleSaveFailed(error) {
      this.$buefy.toast.open({
        message: `Failed to save dossier: ${error.message}`,
        type: 'is-danger',
        duration: 5000,
      });
    },
    buildPrintSections() {
      if (this.printMode === 'single' && this.topLevelPropertyId !== '') {
        const menuItem = this.dossierStore.menuItems.find(item => item.id === this.topLevelPropertyId);

        if (menuItem) {
          return [
            [
              menuItem.label,
              this.dossierStore.currentValue[this.topLevelPropertyId],
            ],
          ];
        }

        return [[]];
      }

      return this.dossierStore.menuItems.map(item => {
        return [
          item.label,
          this.dossierStore.currentValue[item.id],
        ];
      });
    },
    async printDossier() {
      const payload = {
        name: this.entityData.name,
        hide_empty: !this.printEmpty,
        sections: this.buildPrintSections(),
      };

      try {
        const response = await fetch('/api/v1/dossier/print/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        let filename = 'Dossier';

        if (this.entityData.name) {
          filename += ` of ${this.entityData.name}`;
        }

        filename += ` ${getCurrentDateUSFormat()}`;

        if (this.printMode === 'single') {
          const menuItem = this.dossierStore.menuItems.find(item => item.id === this.topLevelPropertyId);

          if (menuItem) {
            filename += ` - ${menuItem.label}`;
          }
        }

        if (this.printEmpty) {
          filename += ` (with empty fields)`;
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Could not print Dossier:', error);
      }
    },
    navigateToFormUpload() {
      // Generate the URL for the FormUploadView with the client ID as a query parameter
      const url = this.$router.resolve({
        name: 'form-upload',
        query: { clientId: this.entityExternalId },
      }).href;
      // Open the URL in a new tab
      window.open(url, '_blank');
    },
    async checkForDraftDossier() {
      try {
        const response = await fetch(`/api/v1/dossier/entity/${this.entityExternalId}/draft/`);
        if (response.ok) {
          const data = await response.json();
          this.draftDossier = data.dossier_data;
          this.hasDraftDossier = !!(data.dossier_data && Object.keys(data.dossier_data).length > 0);
        } else {
          this.hasDraftDossier = false;
        }
      } catch (error) {
        console.error('Error checking for draft dossier:', error);
        this.hasDraftDossier = false;
      }
    },
    navigateToFactSheet() {
      const url = this.$router.resolve({
        name: 'fact-sheet',
        params: { entityExternalId: this.entityExternalId }
      }).href;
      window.open(url, '_blank');
    },
    async navigateToCrmMigration() {
      try {
        const response = await fetchWrapper('/api/v1/crm/mappings/', {
          headers: this.headers,
        });
        console.log('response', response);
        if (response.ok) {
          console.log('response ok');
          const data = await response.json();
          if (data.mappings && Object.keys(data.mappings).length > 0) {
            console.log('crm mappings found', Object.keys(data.mappings));
            this.$router.push({
              name: 'crm-migration',
              params: { entityExternalId: this.entityExternalId }
            });
          } else {
            throw new Error('No CRM mappings found');
          }
        } else {
          throw new Error('No CRM mappings found');
        }
      } catch (error) {
        this.$router.push({
          path: '/advisor/crm-mapping',
        });
      }
    },
    navigateToFinancialPlan() {
      this.$router.push({
        name: 'financial-plan-mockup',
        params: { entityExternalId: this.entityExternalId }
      });
    },
  },
  beforeDestroy() {
    // Clear any active polling interval when component is destroyed
    if (this.taskCheckInterval) {
      clearInterval(this.taskCheckInterval);
      this.taskCheckInterval = null;
    }
  },
};
</script>

<style scoped>
.body {
  --offset: 1rem;
  align-items: start;
  display: flex;
  gap: 2rem;
  margin-top: calc(-1 * var(--offset));
}

.side {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  width: 14rem;
  padding-top: var(--offset);
}

.main {
  flex-grow: 1;
  padding-top: var(--offset);
  min-height: 40rem;
  position: relative;
  overflow: visible;
}

.master-upload {
  width: 60rem;
}

.print-radio {
  cursor: pointer;
}

.print-box {
  padding: 1em;
}

.header-buttons {
  display: flex;
  align-items: center;
  gap: 1rem;
}
</style>
